import { useState } from 'react';
import {
    Button,
    useNotify,
    useRefresh,
    useDataProvider,
    useTranslate,
    useListContext,
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress } from '@mui/material';
import { Delete, ErrorOutline, CheckCircle, DeleteForever } from '@mui/icons-material';
import { SectionField } from '../components';

/**
 * Generic reusable bulk delete dialog for any resource.
 *
 * @param {string} [props.resource] - Optional override for resource name
 * @param {function} [props.getLabel] - Function(record) → string (label displayed in confirm list)
 * @param {string} [props.confirmTitleKey] - i18n key for dialog title
 * @param {string} [props.confirmMessageKey] - i18n key for dialog message
 */
const CustomBulkDeleteDialog = ({
    resource: resourceProp,
    getLabel,
    confirmTitleKey = 'app.common.delete.confirmTitle',
    confirmMessageKey = 'app.common.delete.confirmMessage',
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();

    const { resource: ctxResource, selectedIds, onUnselectItems } = useListContext();
    const resource = resourceProp || ctxResource;

    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [idsToDelete, setIdsToDelete] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        if (!selectedIds?.length) {
            notify(translate('app.common.delete.noSelection') || 'No items selected', { type: 'warning' });
            return;
        }

        try {
            setIdsToDelete(selectedIds);
            const { data } = await dataProvider.getMany(resource, { ids: selectedIds });
            setSelectedRecords(data || []);
            setOpenConfirm(true);
        } catch (error) {
            console.error('[CustomBulkDeleteDialog] getMany failed:', error);
            notify(translate('ra.notification.http_error'), { type: 'error' });
        }
    };

    const handleClose = () => setOpenConfirm(false);

    const count = idsToDelete.length;

    const handleDelete = async () => {
        setLoading(true);
        try {
            await dataProvider.deleteMany(resource, { ids: idsToDelete });
            onUnselectItems?.();
            notify(
                translate('app.misc.bulkDeleted', { count }),
                { type: 'success' }
            );
            refresh();
        } catch (error) {
            console.error('[CustomBulkDeleteDialog] deleteMany error:', error);
            notify(`Error: ${error.message}`, { type: 'error' });
        } finally {
            setOpenConfirm(false);
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                style={{ width: '100%', justifyContent: 'flex-start' }}
                label={translate('app.buttons.delete') || 'Delete'}
                onClick={handleClick}
                color="error"
            >
                <Delete sx={{ color: 'red !important' }} />
            </Button>

            <Dialog open={openConfirm} onClose={handleClose}>
                <DialogTitle>
                    <SectionField
                        label={translate(confirmTitleKey, { smart_count: idsToDelete.length })}
                        icon={DeleteForever}
                    />
                </DialogTitle>

                <DialogContent>
                    <Typography variant="headline2">
                        {translate(confirmMessageKey) || 'Do you really want to delete these items?'}
                    </Typography>

                    {selectedRecords.length > 0 && (
                        <ul style={{ marginLeft: '1em', paddingLeft: '1em', color: '#d32f2f', fontWeight: 'bold' }}>
                            {selectedRecords.map(rec => (
                                <li key={rec.id}>
                                    {getLabel
                                        ? getLabel(rec)
                                        : rec.name || rec.reference || `#${rec.id}`}
                                </li>
                            ))}
                        </ul>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} startIcon={<ErrorOutline />} label={translate('ra.action.cancel')} />
                    {/* <Button onClick={handleDelete} startIcon={<CheckCircle />} label={translate('ra.action.confirm')} /> */}
                    <Button
                        onClick={handleDelete}
                        startIcon={loading ? null : <CheckCircle />}
                        disabled={loading}
                        label={translate('ra.action.confirm')}
                    >
                        {loading ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CircularProgress size={18} color="inherit" />
                                {translate('ra.action.confirm')}
                            </span>
                        ) : (
                            translate('ra.action.confirm')
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomBulkDeleteDialog;
